import { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import mrwilbroadpic from "./../assets/image/mrwilbroad.png";
import EducationTimeline from "./EducationBackground";
import { motion, AnimatePresence } from "framer-motion";
import Certificates from "./Certficates";
import Projects from "./Projects";
import SocialNav from "./SocialNav";
import { Tabs, Tab, Box, Paper, styled } from '@mui/material';
import {
    Info as AboutIcon,
    Code as ProjectsIcon,
    WorkspacePremium as CertIcon,
    Article as BlogIcon
} from '@mui/icons-material';

const StyledTabs = styled(Tabs)({
    '& .MuiTabs-indicator': {
        backgroundColor: '#778da9',
    },
});

const StyledTab = styled(Tab)({
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-selected': {
        color: '#fff',
    },
});

const TabPanel = ({ children, value, index }: { children: React.ReactNode, value: number, index: number }) => {
    return (
        <div hidden={value !== index}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const Introduction = () => {
    const RotationWords = [
        { text: "Software Engineer" },
        { text: "Full Stack Developer" },
        { text: "Web Developer" },
        { text: "Mobile Developer" },
    ];

    const [loopNumber, setLoopNumber] = useState(0);
    const [tabValue, setTabValue] = useState(0);
    const mediumBlue = "#1b263b";
    const accentBlue = "#778da9";

    useEffect(() => {
        const interval = setInterval(() => {
            setLoopNumber((prev) => (prev + 1) % RotationWords.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [RotationWords.length]);

    const displayText = RotationWords[loopNumber].text;

    return (
        <Container fluid className="p-0 m-0" style={{ minHeight: "100vh", position: "relative" }}>
            <Row className="g-0" style={{ minHeight: "100vh", position: "relative", zIndex: 1 }}>
                <Col md={4} className="d-flex flex-column justify-content-center align-items-center text-center p-4">
                    <motion.div
                        className="glass-container p-4 p-md-5 rounded-4 relative"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        style={{
                            backdropFilter: "blur(10px)",
                            border: "1px solid rgba(255,255,255,0.1)",
                            maxWidth: "90%",
                            width: "100%",
                        }}
                    >
                        <motion.div
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 100 }}
                        >
                            <div className="profile-image-container mb-4" style={{ position: "relative" }}>
                                <motion.img
                                    src={mrwilbroadpic}
                                    alt="mrwilbroad"
                                    className="rounded-circle shadow"
                                    whileHover={{ scale: 1.05 }}
                                    style={{
                                        width: "200px",
                                        height: "200px",
                                        objectFit: "cover",
                                        border: `4px solid ${accentBlue}`,
                                    }}
                                />
                                <div style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    borderRadius: "50%",
                                    boxShadow: `0 0 20px ${accentBlue}`,
                                    zIndex: -1
                                }}></div>
                            </div>

                            <h2 className="fw-bold text-white mb-3">Hi, I'm Eng Wilbroad</h2>

                            <div className="dynamic-text-container mb-4" style={{ minHeight: "40px" }}>
                                <AnimatePresence mode="wait">
                                    <motion.h4
                                        key={displayText}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.3 }}
                                        className="fw-semibold"
                                        style={{ color: "white" }}
                                    >
                                        {displayText}
                                        <motion.span
                                            animate={{ opacity: [0, 1, 0] }}
                                            transition={{ repeat: Infinity, duration: 1 }}
                                            style={{ marginLeft: "4px", color: accentBlue }}
                                        >
                                            |
                                        </motion.span>
                                    </motion.h4>
                                </AnimatePresence>
                            </div>

                            <motion.div
                                className="mt-4 d-flex gap-3 justify-content-center flex-wrap"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5 }}
                            >
                                <motion.a
                                    href="mailto:info@ih-africa.com"
                                    className="btn btn-lg px-4"
                                    whileHover={{ y: -3 }}
                                    style={{
                                        background: mediumBlue,
                                        color: "white",
                                        border: `1px solid ${accentBlue}`
                                    }}
                                >
                                    <i className="fa-solid fa-envelope me-2"></i>Mail Me
                                </motion.a>

                                <motion.a
                                    href="https://github.com/mrwilbroad"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-lg px-4"
                                    whileHover={{ y: -3 }}
                                    style={{
                                        background: "transparent",
                                        color: "white",
                                        border: `1px solid ${accentBlue}`
                                    }}
                                >
                                    <i className="fa-brands fa-github me-2"></i>Github
                                </motion.a>
                            </motion.div>
                        </motion.div>
                    </motion.div>
                </Col>

                <Col md={8} className="p-0" style={{ overflowY: "auto", maxHeight: "100vh", background: mediumBlue }}>
                    <Paper sx={{ backgroundColor: 'transparent', color: '#fff', boxShadow: 'none' }}>
                        <StyledTabs
                            value={tabValue}
                            onChange={(_, newValue) => setTabValue(newValue)}
                            aria-label="Profile sections"
                            sx={{ '& .MuiTabs-flexContainer': { justifyContent: 'center' } }}
                        >
                            <StyledTab icon={<AboutIcon />} iconPosition="start" label="About Me" />
                            <StyledTab icon={<ProjectsIcon />} iconPosition="start" label="Projects" />
                            <StyledTab icon={<CertIcon />} iconPosition="start" label="Certifications" />
                            <StyledTab icon={<BlogIcon />} iconPosition="start" label="Blog" />
                        </StyledTabs>
                    </Paper>

                    <TabPanel value={tabValue} index={0}>
                        <EducationTimeline />
                    </TabPanel>
                    <TabPanel value={tabValue} index={1}>
                        <Projects />
                    </TabPanel>
                    <TabPanel value={tabValue} index={2}>
                        <Certificates />
                    </TabPanel>
                    <TabPanel value={tabValue} index={3}>
                       <p>This is Feature coming soon</p>
                    </TabPanel>
                </Col>
            </Row>

            <SocialNav />
        </Container>
    );
};

export default Introduction;